import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  MenuItem,
  TextField,
} from '@mui/material';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import CloseIcon from '@mui/icons-material/Close';
import EmailIcon from '@mui/icons-material/Email';
import LockIcon from '@mui/icons-material/Lock';
import PersonIcon from '@mui/icons-material/Person';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { Controller, type FieldErrors, useForm } from 'react-hook-form';
import { z } from 'zod';
import { UserRole, UserRoleLabels, type CreateUserData, type UpdateUserData } from '@/types/user';
import type { CompanyUserRow } from '@/features/companies/companies.api';

type FormMode = 'create' | 'edit';

const baseSchema = {
  email: z.string().min(1, 'Email é obrigatório').email('Email inválido'),
  fullName: z
    .string()
    .min(2, 'Nome deve ter no mínimo 2 caracteres')
    .max(255, 'Nome deve ter no máximo 255 caracteres'),
  role: z.nativeEnum(UserRole),
};

const passwordSchema = z
  .string()
  .min(12, 'Senha deve ter no mínimo 12 caracteres')
  .max(128, 'Senha deve ter no máximo 128 caracteres')
  .regex(/[A-Z]/, 'Senha deve conter pelo menos uma letra maiúscula')
  .regex(/[a-z]/, 'Senha deve conter pelo menos uma letra minúscula')
  .regex(/[0-9]/, 'Senha deve conter pelo menos um número')
  .regex(/[^A-Za-z0-9]/, 'Senha deve conter pelo menos um caractere especial');

const createUserSchema = z.object({
  ...baseSchema,
  password: passwordSchema,
});

const editUserSchema = z.object({
  ...baseSchema,
  password: z.string().superRefine((value, ctx) => {
    if (value.length === 0) return;

    const result = passwordSchema.safeParse(value);
    if (result.success) return;

    result.error.issues.forEach(issue => {
      ctx.addIssue({
        code: 'custom',
        message: issue.message,
      });
    });
  }),
});

type CreateCompanyUserFormData = z.infer<typeof createUserSchema>;

interface CreateCompanyUserDialogProps {
  open: boolean;
  mode?: FormMode;
  user?: CompanyUserRow;
  onClose: () => void;
  onSubmit: (data: CreateUserData | UpdateUserData) => Promise<void>;
}

export const CreateCompanyUserDialog: React.FC<CreateCompanyUserDialogProps> = ({
  open,
  mode = 'create',
  user,
  onClose,
  onSubmit,
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const isEditMode = mode === 'edit';
  const schema = isEditMode ? editUserSchema : createUserSchema;

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CreateCompanyUserFormData>({
    resolver: async data => {
      try {
        const validatedData = schema.parse(data);
        return { values: validatedData, errors: {} };
      } catch (error) {
        if (error instanceof z.ZodError) {
          const fieldErrors: FieldErrors<CreateCompanyUserFormData> = {};
          error.issues.forEach(issue => {
            const fieldName = issue.path[0] as keyof CreateCompanyUserFormData;
            if (fieldName && !fieldErrors[fieldName]) {
              fieldErrors[fieldName] = {
                type: issue.code,
                message: issue.message,
              };
            }
          });
          return { values: {}, errors: fieldErrors };
        }
        return { values: {}, errors: {} };
      }
    },
    mode: 'onSubmit',
    defaultValues: {
      email: '',
      password: '',
      fullName: '',
      role: UserRole.OPERATIONS,
    },
  });

  useEffect(() => {
    if (!open) return;
    if (isEditMode && user) {
      reset({
        email: user.email,
        password: '',
        fullName: user.fullName,
        role: user.role === 'operation' ? UserRole.OPERATIONS : UserRole.ADMIN,
      });
    } else {
      reset({
        email: '',
        password: '',
        fullName: '',
        role: UserRole.OPERATIONS,
      });
    }
    setSubmitError(null);
    setShowPassword(false);
  }, [isEditMode, open, reset, user]);

  const handleClose = () => {
    if (isSubmitting) return;
    reset();
    setSubmitError(null);
    setShowPassword(false);
    onClose();
  };

  const handleFormSubmit = async (data: CreateCompanyUserFormData) => {
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      if (isEditMode) {
        const updateData: UpdateUserData = {
          fullName: data.fullName,
          role: data.role,
        };
        if (data.password) updateData.password = data.password;
        await onSubmit(updateData);
      } else {
        await onSubmit(data);
      }
      handleClose();
    } catch (error) {
      setSubmitError(
        error instanceof Error
          ? error.message
          : isEditMode
            ? 'Erro ao atualizar usuário. Tente novamente.'
            : 'Erro ao criar usuário. Tente novamente.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      slotProps={{
        paper: {
          sx: { borderRadius: 2 },
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PersonIcon color="primary" />
          {isEditMode ? 'Editar Usuário' : 'Novo Usuário'}
        </Box>
        <IconButton
          onClick={handleClose}
          disabled={isSubmitting}
          size="small"
          sx={{ color: 'text.secondary' }}
          aria-label="Fechar"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <DialogContent>
          {submitError && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {submitError}
            </Alert>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <Controller
              name="fullName"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Nome Completo"
                  fullWidth
                  error={!!errors.fullName}
                  helperText={errors.fullName?.message}
                  disabled={isSubmitting}
                  autoFocus
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <PersonIcon color="action" />
                        </InputAdornment>
                      ),
                    },
                  }}
                />
              )}
            />

            <Controller
              name="email"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Email"
                  type="email"
                  fullWidth
                  error={!!errors.email}
                  helperText={errors.email?.message}
                  disabled={isSubmitting || isEditMode}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <EmailIcon color="action" />
                        </InputAdornment>
                      ),
                    },
                  }}
                />
              )}
            />

            <Controller
              name="password"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={isEditMode ? 'Nova Senha (opcional)' : 'Senha'}
                  type={showPassword ? 'text' : 'password'}
                  fullWidth
                  error={!!errors.password}
                  helperText={
                    errors.password?.message ||
                    (isEditMode
                      ? 'Deixe em branco para manter a senha atual'
                      : 'Mínimo 12 caracteres, incluindo maiúscula, minúscula, número e caractere especial')
                  }
                  disabled={isSubmitting}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockIcon color="action" />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPassword(value => !value)}
                            edge="end"
                            disabled={isSubmitting}
                            aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                          >
                            {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    },
                  }}
                />
              )}
            />

            <Controller
              name="role"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Cargo"
                  select
                  fullWidth
                  error={!!errors.role}
                  helperText={errors.role?.message}
                  disabled={isSubmitting}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <AdminPanelSettingsIcon color="action" />
                        </InputAdornment>
                      ),
                    },
                  }}
                >
                  {Object.values(UserRole).map(role => (
                    <MenuItem key={role} value={role}>
                      {UserRoleLabels[role]}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3, pt: 2 }}>
          <Button onClick={handleClose} disabled={isSubmitting} variant="outlined" color="inherit">
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isSubmitting}
            startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
          >
            {isSubmitting
              ? isEditMode
                ? 'Salvando...'
                : 'Criando...'
              : isEditMode
                ? 'Salvar Alterações'
                : 'Criar Usuário'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
